import { useEffect } from "react";

/*
  ADBA Contact Page — Info + Map only (no form)
  src/Pages/Contact.jsx
*/

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Outfit:wght@300;400;500;600;700;800&display=swap');

:root {
  --red:#CC0000; --red2:#990000; --red3:#E63333; --red5:#FF9999;
  --rg:linear-gradient(135deg,#7B0000 0%,#CC0000 100%);
  --bg:#FAF3F3; --border:#ECDADA; --sub:#6B2020; --text:#180000;
  --fd:'Barlow Condensed',sans-serif;
  --fu:'Outfit',sans-serif;
}
*,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }

/* ════════════════════════════════════
   HERO STRIP
════════════════════════════════════ */
.ct-hero {
  background:#0A0000;
  position:relative; overflow:hidden;
  padding:110px 80px 72px;
  display:flex; align-items:flex-end; justify-content:space-between;
  flex-wrap:wrap; gap:28px;
}
.ct-hero::before {
  content:''; position:absolute; inset:0; pointer-events:none;
  background:
    radial-gradient(ellipse 60% 80% at 85% 55%, rgba(153,0,0,.25), transparent 60%),
    radial-gradient(ellipse 50% 60% at 10% 85%, rgba(100,0,0,.2),  transparent 55%);
}
.ct-hero-grid {
  position:absolute; inset:0; pointer-events:none;
  background-image:
    linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px);
  background-size:56px 56px;
}
.ct-hero-ghost {
  position:absolute; right:-16px; bottom:-48px;
  font-family:var(--fd); font-size:clamp(160px,20vw,280px);
  font-weight:900; letter-spacing:4px; line-height:1;
  color:transparent; -webkit-text-stroke:1px rgba(255,255,255,.038);
  pointer-events:none; user-select:none;
}
.ct-hero-left { position:relative; z-index:2; }
.ct-hero-pill {
  display:inline-flex; align-items:center; gap:8px;
  padding:5px 16px 5px 10px; margin-bottom:22px;
  background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12);
  border-radius:999px;
  font-family:var(--fu); font-size:9px; font-weight:700;
  letter-spacing:2.5px; text-transform:uppercase; color:rgba(255,255,255,.45);
  width:fit-content; animation:ctUp .7s .05s both;
}
.ct-hero-dot {
  width:6px; height:6px; border-radius:50%; background:var(--red5);
  animation:ctPulse 2.4s ease-in-out infinite;
}
@keyframes ctPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.15;transform:scale(.35)}}
.ct-hero h1 {
  font-family:var(--fd); font-weight:900; line-height:.85; letter-spacing:3px;
  animation:ctUp .8s .15s both;
}
.ct-hero h1 .l1 { display:block; font-size:clamp(56px,8vw,110px); color:#fff; }
.ct-hero h1 .l2 {
  display:block; font-size:clamp(56px,8vw,110px);
  background:var(--rg); -webkit-background-clip:text;
  -webkit-text-fill-color:transparent; background-clip:text;
  filter:drop-shadow(0 0 22px rgba(200,0,0,.5));
}
.ct-hero-right {
  position:relative; z-index:2; max-width:380px;
  animation:ctUp .8s .25s both;
}
.ct-hero-sub {
  font-family:var(--fu); font-size:14.5px; line-height:1.82;
  color:rgba(255,255,255,.36); font-weight:300;
}

/* ════════════════════════════════════
   BODY — 2-col
════════════════════════════════════ */
.ct-body {
  background:var(--bg);
  padding:72px 80px 100px;
  display:grid;
  grid-template-columns:1fr 1.35fr;
  gap:60px; align-items:start;
}

/* ─── LEFT: info ─── */
.ct-left { display:flex; flex-direction:column; gap:36px; }

.ct-section-tag {
  display:flex; align-items:center; gap:10px;
  font-family:var(--fu); font-size:9.5px; font-weight:700;
  letter-spacing:2.5px; text-transform:uppercase; color:var(--sub);
  margin-bottom:6px;
}
.ct-section-tag-bar { width:18px; height:2px; background:var(--rg); border-radius:2px; }

.ct-left-title {
  font-family:var(--fd); font-size:clamp(38px,4.5vw,62px); font-weight:900;
  line-height:.9; color:var(--text); letter-spacing:2px;
  animation:ctUp .75s .2s both;
}
.ct-left-title span { color:var(--red2); }

.ct-left-desc {
  font-family:var(--fu); font-size:14.5px; line-height:1.82;
  color:var(--sub); font-weight:300; max-width:380px;
  animation:ctUp .75s .3s both;
}

/* info cards */
.ct-cards { display:flex; flex-direction:column; gap:10px; }

.ct-card {
  display:flex; align-items:center; gap:20px;
  padding:22px 26px; border-radius:18px;
  background:#fff; border:1.5px solid var(--border);
  box-shadow:0 2px 18px rgba(0,0,0,.05);
  text-decoration:none; color:inherit;
  transition:transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s, border-color .3s;
  animation:ctUp .65s both;
}
.ct-card:nth-child(1){animation-delay:.35s;}
.ct-card:nth-child(2){animation-delay:.42s;}
.ct-card:nth-child(3){animation-delay:.49s;}
.ct-card:hover {
  transform:translateY(-5px) translateX(3px);
  box-shadow:0 18px 44px rgba(100,0,0,.12);
  border-color:rgba(204,0,0,.22);
}

.ct-card-icon {
  width:50px; height:50px; border-radius:14px; flex-shrink:0;
  background:rgba(204,0,0,.07); border:1.5px solid rgba(204,0,0,.14);
  display:flex; align-items:center; justify-content:center;
  transition:background .3s, transform .3s;
}
.ct-card:hover .ct-card-icon {
  background:rgba(204,0,0,.14);
  transform:scale(1.08);
}
.ct-card-icon svg {
  width:20px; height:20px;
  stroke:var(--red2); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round;
}

.ct-card-body { display:flex; flex-direction:column; gap:3px; }
.ct-card-label {
  font-family:var(--fu); font-size:9.5px; font-weight:700;
  letter-spacing:2px; text-transform:uppercase; color:var(--sub);
}
.ct-card-val {
  font-family:var(--fu); font-size:15px; font-weight:600; color:var(--text);
  line-height:1.45;
}
.ct-card-arrow {
  margin-left:auto; flex-shrink:0;
  width:32px; height:32px; border-radius:8px;
  background:rgba(204,0,0,.07);
  display:flex; align-items:center; justify-content:center;
  opacity:0; transform:translateX(-6px);
  transition:opacity .28s, transform .28s;
}
.ct-card:hover .ct-card-arrow { opacity:1; transform:translateX(0); }
.ct-card-arrow svg {
  width:14px; height:14px;
  stroke:var(--red2); fill:none; stroke-width:2.5; stroke-linecap:round;
}

/* hours strip */
.ct-hours {
  padding:20px 24px; border-radius:14px;
  background:rgba(204,0,0,.05); border:1.5px solid rgba(204,0,0,.12);
  animation:ctUp .7s .58s both;
}
.ct-hours-title {
  font-family:var(--fu); font-size:9.5px; font-weight:700;
  letter-spacing:2px; text-transform:uppercase; color:var(--red2);
  margin-bottom:10px;
}
.ct-hours-rows { display:flex; flex-direction:column; gap:6px; }
.ct-hours-row {
  display:flex; justify-content:space-between; align-items:center;
  font-family:var(--fu); font-size:13px; color:var(--sub); font-weight:400;
}
.ct-hours-row strong { color:var(--text); font-weight:600; }

/* ─── RIGHT: map ─── */
.ct-right { display:flex; flex-direction:column; gap:16px; animation:ctUp .8s .25s both; }

.ct-map-label {
  display:flex; align-items:center; gap:10px;
  font-family:var(--fu); font-size:9.5px; font-weight:700;
  letter-spacing:2.5px; text-transform:uppercase; color:var(--sub);
}
.ct-map-label-bar { width:18px; height:2px; background:var(--rg); border-radius:2px; }

.ct-map-card {
  border-radius:20px; overflow:hidden;
  border:1.5px solid var(--border);
  box-shadow:0 8px 40px rgba(0,0,0,.1);
  position:relative;
}
.ct-map-card iframe {
  width:100%; height:480px; display:block; border:none;
}

/* address overlay on map */
.ct-map-overlay {
  position:absolute; bottom:16px; left:16px;
  display:flex; align-items:flex-start; gap:12px;
  padding:14px 18px; border-radius:14px;
  background:rgba(255,255,255,.95); backdrop-filter:blur(12px);
  border:1px solid rgba(0,0,0,.08);
  box-shadow:0 4px 20px rgba(0,0,0,.14);
  max-width:280px;
}
.ct-map-overlay-pin {
  width:36px; height:36px; border-radius:10px; flex-shrink:0;
  background:var(--rg);
  display:flex; align-items:center; justify-content:center;
}
.ct-map-overlay-pin svg { width:18px; height:18px; stroke:#fff; fill:none; stroke-width:2; stroke-linecap:round; }
.ct-map-overlay-name {
  font-family:var(--fu); font-size:13px; font-weight:700; color:var(--text);
  line-height:1.3;
}
.ct-map-overlay-addr {
  font-family:var(--fu); font-size:11.5px; color:var(--sub);
  font-weight:300; margin-top:2px; line-height:1.5;
}

/* directions button */
.ct-dir-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:9px;
  padding:13px 28px; border-radius:12px;
  background:var(--rg); color:#fff; text-decoration:none;
  font-family:var(--fu); font-size:13px; font-weight:700; letter-spacing:.3px;
  box-shadow:0 5px 22px rgba(153,0,0,.35);
  transition:transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s;
  align-self:flex-start; border:none; cursor:pointer;
}
.ct-dir-btn:hover { transform:translateY(-3px); box-shadow:0 12px 36px rgba(153,0,0,.5); }
.ct-dir-btn svg { width:15px; height:15px; fill:none; stroke:currentColor; stroke-width:2.5; stroke-linecap:round; }

/* anim */
@keyframes ctUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

/* ════════════════════════════════════
   RESPONSIVE
════════════════════════════════════ */
@media(max-width:1050px){
  .ct-body { grid-template-columns:1fr; gap:48px; padding:56px 40px 80px; }
  .ct-map-card iframe { height:360px; }
  .ct-hero { padding:100px 40px 60px; }
}
@media(max-width:640px){
  .ct-hero { padding:88px 20px 52px; }
  .ct-body { padding:44px 20px 64px; }
  .ct-map-card iframe { height:280px; }
  .ct-map-overlay { display:none; }
}
`;

let _css = false;
const injectCSS = () => {
  if (_css || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.innerHTML = CSS;
  document.head.appendChild(el);
  _css = true;
};

/* ── icons ── */
const MailIcon = () => (
  <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.1 5.18 2 2 0 015.09 3h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L9.09 10.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
);
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
);
const NavIcon = () => (
  <svg viewBox="0 0 24 24"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
);

const ContactPage = () => {
  injectCSS();

  const cards = [
    {
      Icon: MailIcon,
      label: "Mail Us",
      val: "contact@adba.in",
      href: "mailto:contact@adba.in",
    },
    {
      Icon: PhoneIcon,
      label: "Telephone",
      val: "+91 98251 24757",
      href: "tel:+919825124757",
    },
    {
      Icon: PinIcon,
      label: "Address",
      val: "Comfy Circle, Nr. Bhaikaka Statue,\nVallabh Vidyanagar‑388120, Gujarat",
      href: "https://maps.google.com/?q=Comfy+Circle+Vallabh+Vidyanagar+Gujarat",
      external: true,
    },
  ];

  return (
    <>
      {/* HERO */}
      <div className="ct-hero">
        <div className="ct-hero-grid" />
        <div className="ct-hero-ghost">CONTACT</div>

        <div className="ct-hero-left">
          <div className="ct-hero-pill">
            <span className="ct-hero-dot" />
            Anand District Badminton Association
          </div>
          <h1>
            <span className="l1">GET IN</span>
            <span className="l2">TOUCH.</span>
          </h1>
        </div>

        <div className="ct-hero-right">
          <p className="ct-hero-sub">
            Feel free to contact us for any inquiries, collaborations,
            or to join our badminton community. We're eager to hear from
            you and ready to assist!
          </p>
        </div>
      </div>

      {/* BODY */}
      <div className="ct-body">

        {/* LEFT — info */}
        <div className="ct-left">
          <div>
            <div className="ct-section-tag">
              <div className="ct-section-tag-bar" />
              Contact Information
            </div>
            <div className="ct-left-title">REACH<br /><span>US NOW</span></div>
          </div>

          <p className="ct-left-desc">
            Whether you have questions about tournaments, coaching,
            or membership — we're just a message or a call away.
          </p>

          {/* Cards */}
          <div className="ct-cards">
            {cards.map(({ Icon, label, val, href, external }) => (
              <a
                key={label} href={href} className="ct-card"
                target={external ? "_blank" : undefined} rel="noreferrer"
              >
                <div className="ct-card-icon"><Icon /></div>
                <div className="ct-card-body">
                  <span className="ct-card-label">{label}</span>
                  <span className="ct-card-val" style={{ whiteSpace:"pre-line" }}>{val}</span>
                </div>
                <div className="ct-card-arrow"><ArrowIcon /></div>
              </a>
            ))}
          </div>

          {/* Hours */}
          <div className="ct-hours">
            <div className="ct-hours-title">🏸 &nbsp;Training Hours</div>
            <div className="ct-hours-rows">
              <div className="ct-hours-row">
                <span>Monday – Saturday</span>
                <strong>6:00 AM – 9:00 AM</strong>
              </div>
              <div className="ct-hours-row">
                <span>Monday – Saturday</span>
                <strong>5:00 PM – 9:00 PM</strong>
              </div>
              <div className="ct-hours-row">
                <span>Sunday</span>
                <strong>Closed</strong>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — map */}
        <div className="ct-right">
          <div className="ct-map-label">
            <div className="ct-map-label-bar" />
            Our Location
          </div>

          <div className="ct-map-card">
            <iframe
              title="ADBA Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.4!2d72.9195!3d22.5527!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e4fbc5c30c5e5%3A0xcf0af7fdccc2f72e!2sComfy%20Circle!5e0!3m2!1sen!2sin!4v1700000000000"
              allowFullScreen="" loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* overlay badge */}
            <div className="ct-map-overlay">
              <div className="ct-map-overlay-pin"><PinIcon /></div>
              <div>
                <div className="ct-map-overlay-name">Comfy Circle</div>
                <div className="ct-map-overlay-addr">
                  Nr. Bhaikaka Statue, Mahadev,<br />
                  Vallabh Vidyanagar, Anand 388120
                </div>
              </div>
            </div>
          </div>

          {/* Directions button */}
          <a
            className="ct-dir-btn"
            href="https://maps.google.com/?q=Comfy+Circle+Vallabh+Vidyanagar+Gujarat"
            target="_blank" rel="noreferrer"
          >
            <NavIcon />
            Get Directions
          </a>
        </div>

      </div>
    </>
  );
};

export default ContactPage;